import React from "react";
import tila from "assets/lanitila.jpg";
import { Container, Image, Paper, Stack } from "@mantine/core";
import Subtitle from "./Subtitle";
import { useSettings } from "hooks/useSettings";
import { Contact } from "types/intra";
import { formatPhoneReadable } from "@/utils/phoneUtils";

const InfoView: React.FC = () => {
  const { settings } = useSettings();

  // Helper to get contact by role
  const getContactByRole = (role: string): Contact | undefined => {
    return settings?.contacts?.find((c: Contact) => c.role === role && c.isPublic);
  };

  const payment = getContactByRole("payment");
  const mobilePayNumber = payment?.phone || settings?.paymentMobilePayNumber;

  return (
    <>
      <Container size="md">
        <Image src={tila} alt="Lani tila"
               style={{ borderRadius: "8px", display: "block", margin: "0 auto", maxWidth: "100%" }} />
        <Stack gap="md" mt="md">
          <Paper shadow="sm" p="md" radius="md" withBorder
                 style={{ borderLeft: "4px solid var(--mantine-color-violet-0)" }}>
            <Subtitle size="big" title="Tietoja Laneista" />
            <fieldset>
              <legend>Sisällys</legend>
              <ul>
                <li>
                  <a href="#taustaa">Alustus</a>
                </li>
                <li>
                  <a href="#tapahtumapaikka">Tapahtumapaikka</a>
                </li>
                <li>
                  <a href="#ilmoittautuminen">Ilmoittautuminen</a>
                </li>
                <li>
                  <a href="#yleista">Yleistä tietoa</a>
                </li>
                <li>
                  <a href="#maksut">Maksut</a>
                </li>
                <li>
                  <a href="#mitamukaan">Mitä tarvitaan mukaan?</a>
                </li>
                <li>
                  <a href="#poytatila">Pöytätila</a>
                </li>
                <li>
                  <a href="#tournaments">Turnaukset</a>
                </li>
                <li>
                  <a href="#ohjelma">Ohjelma</a>
                </li>
                <li>
                  <a href="#yhteystiedot">Yhteystiedot</a>
                </li>
              </ul>
            </fieldset>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="taustaa" title="Taustaa" />
            <p>
              <b>
                Vuosi 2027 on lähellä ja se tarkoittaa vuotuista tammikuun lanitapahtumaa. Tänäkin vuonna
                kokoonnumme {settings?.venueName ? settings.venueName : "Porin helluntaikirkon alasaliin"} {settings?.venueAddress ? settings.venueAddress.split(",")[0] : "Itsenäisyydenkatu 9"}:ssä. {settings?.eventName || "Tammilanit"} alkaa
                torstaina 21.1. klo 18 ja loppuvat sunnuntaina 24.1. klo 12. Tervetuloa mukaan niin uudet
                gamerit kuin paatuneemmatkin lanirotat. Nähdään Tammilaneilla!{" "}
              </b>
            </p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="tapahtumapaikka" title="Tapahtumapaikka" />
            <p>
              Tapahtumapaikka on {settings?.venueName || "Porin helluntaikirkko"},
              osoitteessa {settings?.venueAddress || "Itsenäisyydenkatu 9, 28100 Pori"}.
            </p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="ilmoittautuminen" title="Ilmoittautuminen" />
            <p>
              Laneille tulee ilmoittautua etukäteen{" "}
              <a href={"https://tammilan.it/registration"}>ilmoittautumislomakkeella</a>. Mukaan mahtuu 63
              ensin ilmoittautunutta. Ilmoittautumisen jälkeen saat sähköpostilla tunnukset paikanvaraukseen.
              <br />
              Huom! Maksa paikkasi heti, tai viimeistään kahden viikon kuluessa. Maksamattomat paikat
              palautetaan vapaasti varattavaksi.
            </p>
            <p>
              Pöytävaraustilanne näkyy <a href={"https://tammilan.it/booking"}> varaussivulla</a>.
            </p>
            <p>Alle 13-vuotiailta tarvitsemme huoltajan tiedot.</p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="yleista" title="Yleistä tietoa" />
            <p>
              Alueella on useita erillisiä tiloja pelaajille sekä yöpyjille. Paikalta löytyy jääkaappi
              eväille, uuni/mikro eväiden lämmittämiseen sekä kahvinkeitin riippuvaisille. Lanit alkavat
              torstaina klo 18. Pöydät ja kova tuoli löytyy paikan päältä. Oman tuolin voi tuoda tullessaan.
              Yöpymistilat löytyvät lanitilojen läheisistä huoneista. Paikan päällä voidaan tehdä yhteisiä
              ruokatilauksia.
            </p>
            <p>
              Muista liittyä{" "}
              <a href={"https://discord.gg/89dss6q5Vz"} target={"_blank"} rel={"noreferrer"}>
                Tammilanit Discordiin
              </a>
              . Laneihin pääset ilmoittautumaan täyttämällä{" "}
              <a href={"https://tammilan.it/registration"}>ilmoittautumislomakkeen</a>. Tapahtuma on
              päihteetön.
            </p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="maksut" title="Maksut" />
            <p>
              Osallistumismaksu on paikkavalinnasta
              riippuen {settings?.pricingStandardSeatPrice || 15}€/{settings?.pricingPremiumSeatPrice || 25}€ jonka voi
              maksaa MobilePaylla numeroon
              {mobilePayNumber && (
                <>
                  <br />
                  <b>{formatPhoneReadable(mobilePayNumber)}</b>
                </>
              )}
              {settings?.paymentBankAccount && (
                <>
                  {" "}tai tilinumeroon <b>{settings.paymentBankAccount}</b>
                  {settings.paymentBankAccountHolder && ` (${settings.paymentBankAccountHolder})`}.
                </>
              )}
              {!mobilePayNumber && !settings?.paymentBankAccount && (
                <>
                  <br />
                  <b>+358 45 346 2128</b> tai tilinumeroon <b>FI41 1807 2961 1628 14</b> (Riku Kuusisto).
                </>
              )}
            </p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="mitamukaan" title="Mitä tarvitaan mukaan?" />
            <ul>
              <li>tietokone tai konsoli ja tarvittavat lisälaitteet ja johdot</li>
              <li>sähköjatkojohto, meiltä saa yhden pistorasiapaikan</li>
              <li>
                Verkkokaapeli, lähin kytkin 1-4 metrin päässä. (Kaapeleita myynnissä 10€/kpl paikan päällä)
              </li>
              <li>Pelituoli (halutessasi)</li>
              <li>Yöpymisvarusteet: patja, makuupussi/peitto jne.</li>
              <li>Omat eväät</li>
              <li>Rahaa ruokaan</li>
            </ul>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="poytatila" title="Pöytätila" />
            <p>Konepaikkoja on kahdenlaisia.</p>
            <ul>
              <li>
                {settings?.pricingStandardSeatLabel || "Peruspaikka"} {settings?.pricingStandardSeatDimensions || "100x75cm"} {settings?.pricingStandardSeatPrice ? `${settings.pricingStandardSeatPrice}€` : "20€"}.
              </li>
              <li>
                {settings?.pricingPremiumSeatLabel || "Siunattu paikka"} {settings?.pricingPremiumSeatDimensions || "120x80cm"} {settings?.pricingPremiumSeatPrice ? `${settings.pricingPremiumSeatPrice}€` : "30€"}.
              </li>
            </ul>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="tournaments" title="Turnaukset" />
            <p>
              Laneissa tullaan järjestämään mm. CS 2-, ja Tekken 3-turnaukset.
              <br />
              Jo toistamiseen järjestääme CS 2 runkosarjapelit syystalvella. Laneilla pääpaino annetaan CS 2
              -turnauksen pudotuspeleille ja finaalille sekä lanien aikana muodostuville pelisessioille.
              Ilmoittautuminen turnauksiin tapahtuu vastaamalla{" "}
              <a href={"https://forms.gle/MkkTzPPmybGeCkPZ6"} target={"_blank"} rel={"noreferrer"}>
                Google Forms -lomakkeeseen
              </a>
              . Finaalit tullaan streamaamaan Tammilanien omalla Twitch-kanavalla. Turnauksien sääntöihin
              pääset{" "}
              <a
                href={
                  "https://docs.google.com/document/d/1xfnjds2oVVWyAZHNe-kgF_mEZTgwxrHUKr9xgTZ0GWk/edit?usp=sharing"
                }
                target={"_blank"}
                rel={"noreferrer"}
              >
                tästä
              </a>
            </p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="ohjelma" title="Ohjelma" />
            <p>
              Huom! Kaikki aikataulut ovat suuntaa antavia. Life-illan ajaksi rauhoitamme lanitilan kokonaan.
            </p>
            <p>
              <b>
                <u>Torstai 21.01.2027</u>
              </b>
            </p>
            <p>
              klo 17 Ovet
              <br />
              klo 19 Aloitusinfot ***Mahdollinen ruokatilaus***
            </p>
            <p>
              <b>
                <u>Perjantai 22.01.2027</u>
              </b>
            </p>
            <p>
              <b>
                <u>Lauantai 23.01.2027</u>
              </b>
            </p>
            <p>
              <b>
                <u>Sunnuntai 24.01.2027</u>
              </b>
            </p>
            <p>
              klo 10 Siivous alkaa
              <br />
              klo 12 <b>PURKU!</b>
            </p>
          </Paper>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Subtitle id="yhteystiedot" title="Yhteystiedot" />

            <p>Tapahtuman järjestää {settings?.organizerName || "Tammilanit-yhteisö"}.</p>
            {settings?.contacts && settings.contacts
              .filter((c: Contact) => c.isPublic)
              .sort((a: Contact, b: Contact) => (a.displayOrder || 0) - (b.displayOrder || 0))
              .map((contact: Contact, index: number) => (
                <p key={contact.id || index}>
                  {contact.notes || contact.role}
                  <br />
                  {contact.name}
                  {contact.phone && (
                    <>
                      <br />
                      {formatPhoneReadable(contact.phone)}
                    </>
                  )}
                  {contact.email && (
                    <>
                      <br />
                      {contact.email}
                    </>
                  )}
                </p>
              ))}
            {(!settings?.contacts || settings.contacts.length === 0) && (
              <>
                <p>
                  Tapahtuman koordinaattori
                  <br />
                  Pekka Peltomäki
                  <br />
                  +358 40 081 1178
                  <br />
                  pekkapeltomaki (at) msn.com
                </p>
                <p>
                  Tekninen suunnittelu ja tuki
                  <br />
                  Riku Kuusisto
                  <br />
                  +358 45 346 2128
                  <br />
                  rikse94@gmail.com
                </p>
              </>
            )}
          </Paper>
        </Stack>
      </Container>
    </>
  );
};

export default InfoView;
